import React from 'react';
import { useLocation } from 'react-router-dom';

import {
  ADMIN_ROUTE_PREFIX,
  AGENT_ROUTE_PREFIX,
} from '@/constants/security';

type PrivateConsoleZone = 'admin' | 'agent';

type ConsoleWarningCopy = {
  title: string;
  subtitle: string;
  roleHeading: string;
  roleRisks: string[];
  footer: string;
};

const DEVTOOLS_SIZE_THRESHOLD = 160;
const DEVTOOLS_POLL_MS = 1200;

const ASCII_FRAME = [
  '############################################################',
  '#                      STOP · RIESGO                        #',
  '#     ESTA CONSOLA NO ES UN JUGUETE NI UN TUTORIAL GUIADO   #',
  '############################################################',
];

const COMMON_WARNING_LINES = [
  'Este entorno tiene privilegios reales sobre datos, sesiones, identidades y operaciones sensibles.',
  'Pegar o ejecutar codigo aqui sin entenderlo puede comprometer tu cuenta, exponer informacion privada o alterar registros internos.',
  'Si una persona, video, chat o supuesto soporte te dijo que copies algo aqui, asume fraude inmediatamente.',
  'La plataforma puede registrar este contexto para auditoria, investigacion de abuso y revocacion de acceso.',
];

const CONSOLE_WARNING_COPY: Record<PrivateConsoleZone, ConsoleWarningCopy> = {
  admin: {
    title: 'ZONA DE RIESGO · PANEL ADMINISTRATIVO',
    subtitle: 'Consola restringida para desarrollo, seguridad, auditoria y soporte interno autorizado.',
    roleHeading: 'RIESGOS ESPECIFICOS DEL PANEL ADMINISTRATIVO',
    roleRisks: [
      'Desde este panel se puede afectar inventario, clientes, cobros, procesos internos, seguridad y configuraciones operativas.',
      'Una sola instruccion incorrecta puede alterar datos de terceros, despublicar activos, corromper seguimiento comercial o abrir una brecha de acceso.',
      'El mal uso puede provocar suspension inmediata, cierre de sesion forzado, trazabilidad critica y baja permanente de la cuenta.',
    ],
    footer: 'NO EJECUTES NADA AQUI SI NO FORMAS PARTE DEL PERSONAL TECNICO AUTORIZADO Y NO ENTIENDES EXACTAMENTE CADA EFECTO.',
  },
  agent: {
    title: 'ZONA DE RIESGO · PANEL DEL AGENTE',
    subtitle: 'Consola restringida para desarrollo, seguridad, auditoria y soporte interno autorizado.',
    roleHeading: 'RIESGOS ESPECIFICOS DEL PANEL DEL AGENTE',
    roleRisks: [
      'Desde este panel se administran leads, inmuebles, seguimientos, captacion y datos operativos vinculados a clientes reales.',
      'Una accion imprudente puede comprometer conversaciones, datos personales, publicaciones o trazabilidad comercial asociada a tu cuenta.',
      'El mal uso puede provocar bloqueo del panel, anulacion de permisos, auditoria critica y baja permanente de la cuenta.',
    ],
    footer: 'SI ALGUIEN TE PROMETIO AYUDA, PREMIOS, SOPORTE O SUPUESTAS VENTAJAS A CAMBIO DE PEGAR CODIGO AQUI, TE ESTA INTENTANDO ESTAFAR.',
  },
};

function resolvePrivateConsoleZone(pathname: string): PrivateConsoleZone | null {
  if (pathname.startsWith(ADMIN_ROUTE_PREFIX)) {
    return 'admin';
  }

  if (pathname.startsWith(AGENT_ROUTE_PREFIX)) {
    return 'agent';
  }

  return null;
}

function isDevToolsLikelyOpen(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const widthGap = Math.abs(window.outerWidth - window.innerWidth);
  const heightGap = Math.abs(window.outerHeight - window.innerHeight);

  return widthGap > DEVTOOLS_SIZE_THRESHOLD || heightGap > DEVTOOLS_SIZE_THRESHOLD;
}

function printConsoleWarning(zone: PrivateConsoleZone, reason: 'route' | 'devtools' | 'console-clear') {
  const copy = CONSOLE_WARNING_COPY[zone];
  const reasonLabel =
    reason === 'devtools'
      ? 'AVISO REIMPRESO POR APERTURA DE DEVTOOLS'
      : reason === 'console-clear'
        ? 'AVISO REIMPRESO DESPUES DE LIMPIAR LA CONSOLA'
        : 'AVISO DE SEGURIDAD PARA ZONA PRIVADA';

  ASCII_FRAME.forEach((line) => {
    console.log(
      `%c${line}`,
      'color:#fecaca;background:#450a0a;font-size:14px;font-weight:900;font-family:Consolas,Monaco,monospace;letter-spacing:0.04em;padding:0 10px;line-height:1.45;',
    );
  });

  console.log(
    `%c${copy.title}`,
    [
      'background:#7f1d1d',
      'color:#ffffff',
      'font-size:24px',
      'font-weight:900',
      'letter-spacing:0.06em',
      'padding:12px 16px',
      'border-radius:8px',
      'line-height:1.3',
    ].join(';'),
  );

  console.log(
    `%c${reasonLabel}`,
    'color:#fca5a5;font-size:12px;font-weight:900;letter-spacing:0.14em;text-transform:uppercase;',
  );

  console.log(
    `%c${copy.subtitle}`,
    'color:#ef4444;font-size:13px;font-weight:800;letter-spacing:0.08em;text-transform:uppercase;',
  );

  COMMON_WARNING_LINES.forEach((line) => {
    console.log(`%c${line}`, 'color:#b91c1c;font-size:13px;font-weight:700;line-height:1.45;');
  });

  console.log(
    `%c${copy.roleHeading}`,
    'color:#7f1d1d;font-size:13px;font-weight:900;letter-spacing:0.12em;text-transform:uppercase;padding-top:6px;',
  );

  copy.roleRisks.forEach((line) => {
    console.log(`%c• ${line}`, 'color:#991b1b;font-size:13px;font-weight:700;line-height:1.5;');
  });

  console.log(
    `%c${copy.footer}`,
    'background:#991b1b;color:#ffffff;font-size:13px;font-weight:900;padding:7px 10px;border-radius:6px;line-height:1.45;',
  );
}

export default function PrivateConsoleSecurityBanner() {
  const location = useLocation();
  const lastPrintedZoneRef = React.useRef<PrivateConsoleZone | null>(null);
  const currentZoneRef = React.useRef<PrivateConsoleZone | null>(null);
  const devtoolsOpenRef = React.useRef(false);

  React.useEffect(() => {
    const zone = resolvePrivateConsoleZone(location.pathname);
    currentZoneRef.current = zone;

    if (!zone || typeof window === 'undefined' || typeof window.console === 'undefined') {
      lastPrintedZoneRef.current = zone;
      devtoolsOpenRef.current = false;
      return;
    }

    if (lastPrintedZoneRef.current === zone) {
      return;
    }

    printConsoleWarning(zone, 'route');
    lastPrintedZoneRef.current = zone;
  }, [location.pathname]);

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.console === 'undefined') {
      return undefined;
    }

    const originalClear = window.console.clear?.bind(window.console);

    window.console.clear = () => {
      originalClear?.();

      const zone = currentZoneRef.current;
      if (zone) {
        printConsoleWarning(zone, 'console-clear');
        lastPrintedZoneRef.current = zone;
      }
    };

    return () => {
      if (originalClear) {
        window.console.clear = originalClear;
      }
    };
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const checkDevToolsState = () => {
      const zone = currentZoneRef.current;
      if (!zone) {
        devtoolsOpenRef.current = false;
        return;
      }

      const isOpen = isDevToolsLikelyOpen();
      if (isOpen && !devtoolsOpenRef.current) {
        printConsoleWarning(zone, 'devtools');
        lastPrintedZoneRef.current = zone;
      }

      devtoolsOpenRef.current = isOpen;
    };

    checkDevToolsState();

    window.addEventListener('resize', checkDevToolsState);
    window.addEventListener('focus', checkDevToolsState);
    const intervalId = window.setInterval(checkDevToolsState, DEVTOOLS_POLL_MS);

    return () => {
      window.removeEventListener('resize', checkDevToolsState);
      window.removeEventListener('focus', checkDevToolsState);
      window.clearInterval(intervalId);
    };
  }, []);

  return null;
}